import React, { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import TablePagination from '@mui/material/TablePagination'
import Skeleton from '@mui/material/Skeleton'
import palette from '../../../theme/palette'
import { BUCKETS, count } from './stuckUtils'

/**
 * One of the mail's three tables (courier-wise / merchant-wise / status-wise): a name column, the four age
 * buckets, Total and Breached, busiest first, with the mail's "Total" row pinned at the bottom.
 * rows: [{ key, name, group?, d1To3, d3To5, d5To15, d15Plus, total, breached }]
 */
export default function StuckGroupTable({ rows = [], nameLabel, renderName, loading, onRowClick, emptyText = 'Nothing stuck in this scope' }) {
  const [orderBy, setOrderBy] = useState('total')
  const [order, setOrder] = useState('desc')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const cols = [
    { key: 'name', label: nameLabel, text: true },
    ...BUCKETS.map(b => ({ key: b.rowKey, label: b.label })),
    { key: 'total', label: 'Total' },
    { key: 'breached', label: 'Past expected' },
  ]

  const sorted = useMemo(() => {
    const dir = order === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const av = a[orderBy], bv = b[orderBy]
      if (av === bv) return dir * ((a.total ?? 0) - (b.total ?? 0))
      const cmp = typeof av === 'number' ? av - bv : String(av ?? '').localeCompare(String(bv ?? ''))
      return dir * cmp
    })
  }, [rows, orderBy, order])

  const totals = useMemo(() => rows.reduce((t, r) => {
    BUCKETS.forEach(b => { t[b.rowKey] += r[b.rowKey] || 0 })
    t.total += r.total || 0
    t.breached += r.breached || 0
    return t
  }, { d1To3: 0, d3To5: 0, d5To15: 0, d15Plus: 0, total: 0, breached: 0 }), [rows])

  const paged = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  if (loading) return <Box sx={{ p: 2 }}>{[...Array(6)].map((_, i) => <Skeleton key={i} height={36} sx={{ mb: 0.5 }} />)}</Box>

  const numCell = (v, strong) => <span style={{ fontWeight: strong ? 700 : 400 }}>{count(v)}</span>

  return (
    <>
      <TableContainer>
        <Table size='small' sx={{ '& td, & th': { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              {cols.map(c => (
                <TableCell key={c.key} align={c.text ? 'left' : 'right'} sx={{ bgcolor: palette.grey[200], fontWeight: 700, color: 'text.secondary' }}>
                  <TableSortLabel
                    active={orderBy === c.key} direction={orderBy === c.key ? order : 'desc'}
                    onClick={() => { setOrder(orderBy === c.key && order === 'desc' ? 'asc' : 'desc'); setOrderBy(c.key); setPage(0) }}
                  >
                    {c.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((r) => (
              <TableRow key={r.key} hover onClick={onRowClick ? () => onRowClick(r) : undefined} sx={{ cursor: onRowClick ? 'pointer' : 'default' }}>
                <TableCell>{renderName ? renderName(r) : (r.name || r.key || '—')}</TableCell>
                {BUCKETS.map(b => <TableCell key={b.key} align='right'>{numCell(r[b.rowKey])}</TableCell>)}
                <TableCell align='right'>{numCell(r.total, true)}</TableCell>
                <TableCell align='right'>
                  <span style={{ color: r.breached ? palette.error.dark : undefined, fontWeight: r.breached ? 600 : 400 }}>{count(r.breached)}</span>
                </TableCell>
              </TableRow>
            ))}
            {!paged.length && (
              <TableRow><TableCell colSpan={cols.length} align='center' sx={{ py: 4, color: 'text.disabled' }}>{emptyText}</TableCell></TableRow>
            )}
            {rows.length > 0 && (
              <TableRow sx={{ '& td': { bgcolor: palette.grey[100], fontWeight: 700, borderTop: 2, borderColor: 'divider' } }}>
                <TableCell>Total</TableCell>
                {BUCKETS.map(b => <TableCell key={b.key} align='right'>{count(totals[b.rowKey])}</TableCell>)}
                <TableCell align='right'>{count(totals.total)}</TableCell>
                <TableCell align='right'>{count(totals.breached)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length > rowsPerPage && (
        <TablePagination
          component='div' count={rows.length} page={page} rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      )}
    </>
  )
}
